import { Box, Text, useColorModeValue } from '@chakra-ui/react';
import type { UseQueryResult } from '@tanstack/react-query';
import React from 'react';

import type { TimeChartData, TimeChartItem } from 'ui/shared/chart/types';

import dayjs from 'lib/date/dayjs';
import ChartArea from 'ui/shared/chart/ChartArea';
import ChartLine from 'ui/shared/chart/ChartLine';
import ChartTooltip from 'ui/shared/chart/ChartTooltip';
import useTimeChartController from 'ui/shared/chart/useTimeChartController';
import ContentLoader from 'ui/shared/ContentLoader';
import DataFetchAlert from 'ui/shared/DataFetchAlert';

const LINE_COLOR = '#14DCAC';
const CHART_HEIGHT = '113px';
const CHART_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };
const formatTooltipValue = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 2, notation: 'compact' });

const HomeStatsChart = ({ data, isError, isPending }: UseQueryResult<TimeChartData>) => {
  if (isPending) {
    return <ContentLoader h={ CHART_HEIGHT }/>;
  }

  if (isError) {
    return <DataFetchAlert fontSize="xs" p={ 3 }/>;
  }

  if (!data?.[0]?.items?.length) {
    return <Text fontSize="xs">No data</Text>;
  }

  const chartItems = data[0].items;

  return (
    <Box h={ CHART_HEIGHT }>
      <ChartCanvas chartItems={ chartItems }/>
    </Box>
  );
};

interface ChartCanvasProps {
  chartItems: Array<TimeChartItem>;
}

const ChartCanvas = ({ chartItems }: ChartCanvasProps) => {
  const [ overlayEl, setOverlayEl ] = React.useState<SVGRectElement | null>(null);
  const pointStroke = useColorModeValue('#000000', '#FFFFFF');
  const chartData = React.useMemo(() => ([ {
    items: chartItems.map((item) => ({
      ...item,
      dateLabel: dayjs(item.date).format('D MMM YYYY'),
    })),
    name: 'Tx/day',
    valueFormatter: formatTooltipValue,
  } ]), [ chartItems ]);

  const { rect, ref, axes, innerHeight, innerWidth, chartMargin } = useTimeChartController({
    data: chartData,
    margin: CHART_MARGIN,
    axesConfig: {
      x: { ticks: 6 },
      y: { ticks: 4, nice: true, noLabel: true },
    },
  });

  const handleOverlayRef = React.useCallback((node: SVGRectElement | null) => {
    setOverlayEl(node);
  }, []);

  return (
    <svg width="100%" height="100%" ref={ ref } cursor="pointer">
      <g transform={ `translate(${ chartMargin.left || 0 },${ chartMargin.top || 0 })` } opacity={ rect ? 1 : 0 }>
        <ChartArea
          id="homepage-daily-transactions"
          data={ chartItems }
          xScale={ axes.x.scale }
          yScale={ axes.y.scale }
          color={ LINE_COLOR }
          noAnimation
        />
        <ChartLine
          data={ chartItems }
          xScale={ axes.x.scale }
          yScale={ axes.y.scale }
          stroke={ LINE_COLOR }
          strokeWidth={ 2.56 }
          animation="left"
        />
        <ChartTooltip
          anchorEl={ overlayEl }
          width={ innerWidth }
          height={ innerHeight }
          xScale={ axes.x.scale }
          yScale={ axes.y.scale }
          data={ chartData }
          noAnimation
          lineColor={ LINE_COLOR }
          lineDasharray="2 2"
          lineStrokeWidth={ 1.5 }
          pointFill={ LINE_COLOR }
          pointStroke={ pointStroke }
          pointStrokeWidth={ 2 }
          pointRadius={ 3.5 }
        />
        <rect
          ref={ handleOverlayRef }
          width={ innerWidth }
          height={ innerHeight }
          fill="transparent"
          pointerEvents="all"
        />
      </g>
    </svg>
  );
};

export default React.memo(HomeStatsChart);
